import { inferMatchTypeCategory } from '../../../../utils/matchTypeCatalog';
import { getMapPoolForSeason } from '../../../../data/seasons';
import mapList from '../../../../data/mapList';

export const MODE_DEFINITIONS = [
  { id: 'HP', label: 'Hardpoint', mapKey: 'hardpoint' },
  { id: 'SND', label: 'Search & Destroy', mapKey: 'snd' },
  { id: 'CTL', label: 'Control', mapKey: 'control' },
];

export const DATASET_FILTERS = [
  { value: 'ALL', label: 'All' },
  { value: 'TOURNAMENT', label: 'Tournament' },
  { value: 'SCRIM', label: 'Scrim' },
];

const EDGE_THRESHOLDS = {
  strong: 15,
  regular: 5,
};

const CONFIDENCE_FACTORS = [
  { min: 10, label: 'High', factor: 1 },
  { min: 5, label: 'Moderate', factor: 0.85 },
  { min: 3, label: 'Low', factor: 0.7 },
  { min: 1, label: 'Very Low', factor: 0.5 },
  { min: 0, label: 'No Data', factor: 0 },
];

export const normalizeText = (value) => String(value || '').trim().toUpperCase();

export const normalizeNameKey = (value) =>
  normalizeText(value)
    .replace(/&/g, 'AND')
    .replace(/[^A-Z0-9]+/g, ' ')
    .trim()
    .replace(/\s+/g, ' ');

const normalizeIdentityId = (value) =>
  String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

export const normalizeMode = (value) => {
  const raw = normalizeText(value);
  if (raw.includes('HP') || raw === 'HARDPOINT') return 'HP';
  if (raw.includes('SND') || raw.includes('SEARCH')) return 'SND';
  if (raw.includes('CTL') || raw === 'CONTROL') return 'CTL';
  return MODE_DEFINITIONS.some((mode) => mode.id === raw) ? raw : '';
};

export const normalizeResult = (value) => {
  const raw = normalizeText(value);
  if (raw === 'WIN' || raw === 'W') return 'WIN';
  if (raw === 'LOSS' || raw === 'LOSE' || raw === 'L') return 'LOSS';
  return '';
};

export const toDateValue = (value) => {
  if (!value) return null;
  if (typeof value?.toDate === 'function') {
    const date = value.toDate();
    return Number.isNaN(date.getTime()) ? null : date;
  }
  if (typeof value?.seconds === 'number') {
    const date = new Date(value.seconds * 1000);
    return Number.isNaN(date.getTime()) ? null : date;
  }
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const getTimestamp = (value) => toDateValue(value)?.getTime?.() || 0;

export const formatPercent = (value) => (
  typeof value === 'number' && Number.isFinite(value) ? `${Math.round(value)}%` : 'No data'
);

export const formatRecord = (record) => (
  record?.played ? `${record.wins}-${record.losses}` : '-'
);

export const getSampleConfidence = (played = 0) => {
  const count = Number(played) || 0;
  return CONFIDENCE_FACTORS.find((item) => count >= item.min) || CONFIDENCE_FACTORS[CONFIDENCE_FACTORS.length - 1];
};

export const getMatchupLabel = (delta, { hasOurData = true, hasOpponentData = true } = {}) => {
  if (!hasOurData && !hasOpponentData) return 'No recorded data';
  if (!hasOurData || !hasOpponentData || typeof delta !== 'number' || !Number.isFinite(delta)) {
    return 'Insufficient data';
  }
  if (delta >= EDGE_THRESHOLDS.strong) return 'Strong Our Team Edge';
  if (delta >= EDGE_THRESHOLDS.regular) return 'Our Team Edge';
  if (delta <= -EDGE_THRESHOLDS.strong) return 'Strong Opponent Edge';
  if (delta <= -EDGE_THRESHOLDS.regular) return 'Opponent Edge';
  return 'Even';
};

export const getDatasetType = (row = {}) => {
  const category = normalizeText(row.MatchCategory || row.matchCategory);
  if (category === 'OFFICIAL' || category === 'TOURNAMENT') return 'TOURNAMENT';
  if (category === 'PRACTICE' || category === 'SCRIM') return 'SCRIM';

  const inferred = inferMatchTypeCategory(row.MatchType || row.matchType || row.Type || row.type || '');
  return inferred === 'PRACTICE' ? 'SCRIM' : 'TOURNAMENT';
};

export const rowMatchesDataset = (row, datasetFilter) => {
  if (!datasetFilter || datasetFilter === 'ALL') return true;
  return getDatasetType(row) === datasetFilter;
};

export const rowMatchesOpponent = (row = {}, opponentName = '', opponentId = '') => {
  const selected = typeof opponentName === 'object'
    ? opponentName
    : { id: opponentId, name: opponentName };
  const selectedIds = [
    selected.id,
    selected.opponentId,
    selected.teamId,
    selected.sourceTeamId,
  ].map(normalizeIdentityId).filter(Boolean);
  const selectedNameKeys = [
    selected.name,
    selected.opponentName,
    selected.teamName,
    selected.label,
    ...(Array.isArray(selected.aliases) ? selected.aliases : []),
    ...(Array.isArray(selected.names) ? selected.names : []),
  ].map(normalizeNameKey).filter(Boolean);

  if (!selectedIds.length && !selectedNameKeys.length) return false;

  const recordIds = [
    row.TeamId,
    row.teamId,
    row.TeamID,
    row.OpponentId,
    row.opponentId,
    row.OpponentID,
    row.VSTeamId,
    row.vsTeamId,
    row.EnemyTeamId,
    row.enemyTeamId,
  ].map(normalizeIdentityId).filter(Boolean);

  if (selectedIds.length && recordIds.length && recordIds.some((id) => selectedIds.includes(id))) {
    return true;
  }

  return [
    row.Team,
    row.TeamName,
    row.team,
    row.teamName,
    row.Opponent,
    row.VS,
    row.EnemyTeam,
    row.opponent,
    row.vs,
  ]
    .map(normalizeNameKey)
    .filter(Boolean)
    .some((key) => selectedNameKeys.includes(key));
};

export const buildSelectedOpponentIdentity = (selectedRoom = {}, opponentOptions = []) => {
  const byId = (opponentOptions || []).find((option) => (
    normalizeIdentityId(option?.id || option?.teamId) === normalizeIdentityId(selectedRoom?.opponentId)
  ));
  const byName = (opponentOptions || []).find((option) => (
    normalizeNameKey(option?.name || option?.teamName || option?.label) === normalizeNameKey(selectedRoom?.opponentName)
  ));
  const configured = byId || byName || {};
  const names = [
    selectedRoom?.opponentName,
    configured.name,
    configured.teamName,
    configured.label,
    configured.shortName,
    configured.displayName,
  ];
  return {
    id: selectedRoom?.opponentId || configured.id || configured.teamId || '',
    name: selectedRoom?.opponentName || configured.name || configured.teamName || configured.label || '',
    teamId: configured.teamId || configured.id || '',
    region: configured.region || selectedRoom?.opponentRegion || '',
    aliases: [
      ...names,
      ...(Array.isArray(configured.aliases) ? configured.aliases : []),
      ...(Array.isArray(configured.altNames) ? configured.altNames : []),
    ].filter(Boolean),
  };
};

export const rowPrimaryMatchesOpponent = (row = {}, selectedOpponent = {}) => {
  const selectedNameKeys = [
    selectedOpponent.name,
    selectedOpponent.opponentName,
    selectedOpponent.teamName,
    selectedOpponent.label,
    ...(Array.isArray(selectedOpponent.aliases) ? selectedOpponent.aliases : []),
    ...(Array.isArray(selectedOpponent.names) ? selectedOpponent.names : []),
  ].map(normalizeNameKey).filter(Boolean);

  if (!selectedNameKeys.length) return false;

  return [row.Team, row.TeamName, row.team, row.teamName]
    .map(normalizeNameKey)
    .filter(Boolean)
    .some((key) => selectedNameKeys.includes(key));
};

export const getOpponentMatchDedupeKey = (row = {}) => {
  if (row.matchGroupId) return `group:${row.matchGroupId}`;
  const modeId = row.ModeID || row.id || '';
  if (modeId && row.mirrorId) {
    return `mirror:${[modeId, row.mirrorId].map(String).sort().join(':')}`;
  }
  if (modeId) return `mode:${modeId}`;
  return `fallback:${row.Team || row.TeamName || row.EnemyTeam || row.Opponent || row.VS || ''}|${row.Mode || ''}|${row.Map || ''}|${row.Date || ''}|${row.PF || ''}|${row.PA || ''}`;
};

export const normalizeMatchRow = (row = {}, source = 'team') => {
  const result = normalizeResult(row.WinLose || row.Result || row.result);
  if (!result) return null;

  const mode = normalizeMode(row.Mode || row.mode || row.ModeID || row.modeId);
  const map = String(row.Map || row.map || row.selectedMap || '').trim();
  if (!mode || !map) return null;

  return {
    ...row,
    source,
    mode,
    map,
    result,
    date: row.Date || row.date || row.matchDate || row.createdAt || '',
    timestamp: getTimestamp(row.Date || row.date || row.matchDate || row.createdAt),
    matchType: row.MatchType || row.matchType || '',
    matchCategory: row.MatchCategory || row.matchCategory || '',
    datasetType: getDatasetType(row),
  };
};

export const calculateRecord = (rows = []) => {
  const wins = rows.filter((row) => row.result === 'WIN').length;
  const losses = rows.filter((row) => row.result === 'LOSS').length;
  const played = wins + losses;
  const winRate = played ? (wins / played) * 100 : null;
  return {
    wins,
    losses,
    played,
    winRate,
    confidence: getSampleConfidence(played),
  };
};

export const calculateRecentForm = (rows = [], limit = 5) => {
  const recent = [...rows]
    .sort((a, b) => b.timestamp - a.timestamp)
    .slice(0, limit);
  const wins = recent.filter((row) => row.result === 'WIN').length;
  const losses = recent.filter((row) => row.result === 'LOSS').length;
  return {
    wins,
    losses,
    total: recent.length,
    label: recent.length ? `${wins}-${losses} last ${recent.length}` : 'No recent data',
    sequence: recent.map((row) => ({
      result: row.result === 'WIN' ? 'W' : 'L',
      mode: row.mode,
      map: row.map,
    })),
  };
};

const getCanonicalMapsForMode = (seasonKey, modeId) => {
  const mode = MODE_DEFINITIONS.find((item) => item.id === modeId);
  const configured = getMapPoolForSeason(seasonKey, modeId);
  const fromCatalog = (mapList[mode?.mapKey] || []).map((map) => map.name);
  const names = [...configured, ...fromCatalog]
    .map((name) => String(name || '').trim())
    .filter(Boolean);
  const seen = new Set();
  return names.filter((name) => {
    const key = normalizeNameKey(name);
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

export const strengthScore = (record) => {
  if (!record?.played || typeof record.winRate !== 'number') return 0;
  return record.winRate * record.confidence.factor;
};

const isOverviewEligible = (ourRecord, opponentRecord) => (
  (ourRecord.played >= 3 && opponentRecord.played >= 3) ||
  (ourRecord.played + opponentRecord.played >= 6 && ourRecord.played > 0 && opponentRecord.played > 0)
);

const getMostPlayedCount = (mapRecords = []) => (
  mapRecords.reduce((max, row) => Math.max(max, row.our.played, row.opponent.played), 0)
);

export const buildMatchRoomAnalytics = ({
  teamRows = [],
  opponentRows = [],
  seasonKey,
  datasetFilter = 'ALL',
} = {}) => {
  const ourRows = teamRows
    .filter((row) => rowMatchesDataset(row, datasetFilter))
    .map((row) => normalizeMatchRow(row, 'team'))
    .filter(Boolean);

  const theirRows = opponentRows
    .filter((row) => rowMatchesDataset(row, datasetFilter))
    .map((row) => normalizeMatchRow(row, 'opponent'))
    .filter(Boolean);

  const modes = MODE_DEFINITIONS.map((mode) => {
    const ourModeRows = ourRows.filter((row) => row.mode === mode.id);
    const opponentModeRows = theirRows.filter((row) => row.mode === mode.id);
    const our = calculateRecord(ourModeRows);
    const opponent = calculateRecord(opponentModeRows);
    const delta = our.played && opponent.played ? our.winRate - opponent.winRate : null;

    const maps = getCanonicalMapsForMode(seasonKey, mode.id).map((mapName) => {
      const mapKey = normalizeNameKey(mapName);
      const ourMapRows = ourModeRows.filter((row) => normalizeNameKey(row.map) === mapKey);
      const opponentMapRows = opponentModeRows.filter((row) => normalizeNameKey(row.map) === mapKey);
      const ourMap = calculateRecord(ourMapRows);
      const opponentMap = calculateRecord(opponentMapRows);
      const mapDelta = ourMap.played && opponentMap.played ? ourMap.winRate - opponentMap.winRate : null;
      const combinedPlayed = ourMap.played + opponentMap.played;
      return {
        mode: mode.id,
        modeLabel: mode.label,
        map: mapName,
        our: {
          ...ourMap,
          usage: our.played ? (ourMap.played / our.played) * 100 : null,
          recent: calculateRecentForm(ourMapRows, 5),
          strengthScore: strengthScore(ourMap),
        },
        opponent: {
          ...opponentMap,
          usage: opponent.played ? (opponentMap.played / opponent.played) * 100 : null,
          recent: calculateRecentForm(opponentMapRows, 5),
          strengthScore: strengthScore(opponentMap),
        },
        delta: mapDelta,
        combinedPlayed,
        confidence: getSampleConfidence(combinedPlayed),
        matchupLabel: getMatchupLabel(mapDelta, {
          hasOurData: ourMap.played > 0,
          hasOpponentData: opponentMap.played > 0,
        }),
        overviewEligible: isOverviewEligible(ourMap, opponentMap),
      };
    });

    const mostPlayedCount = getMostPlayedCount(maps);

    return {
      ...mode,
      our,
      opponent,
      delta,
      matchupLabel: getMatchupLabel(delta, {
        hasOurData: our.played > 0,
        hasOpponentData: opponent.played > 0,
      }),
      ourRecent: calculateRecentForm(ourModeRows, 5),
      opponentRecent: calculateRecentForm(opponentModeRows, 5),
      maps: maps.map((map) => ({
        ...map,
        our: {
          ...map.our,
          mostPlayed: mostPlayedCount > 0 && map.our.played === mostPlayedCount,
        },
        opponent: {
          ...map.opponent,
          mostPlayed: mostPlayedCount > 0 && map.opponent.played === mostPlayedCount,
        },
      })),
      ourStrongestMap: maps
        .filter((map) => map.our.played > 0)
        .sort((a, b) => b.our.strengthScore - a.our.strengthScore || b.our.played - a.our.played)[0] || null,
      opponentStrongestMap: maps
        .filter((map) => map.opponent.played > 0)
        .sort((a, b) => b.opponent.strengthScore - a.opponent.strengthScore || b.opponent.played - a.opponent.played)[0] || null,
      ourMostPlayedMaps: maps.filter((map) => map.our.mostPlayed && map.our.played > 0),
      opponentMostPlayedMaps: maps.filter((map) => map.opponent.mostPlayed && map.opponent.played > 0),
    };
  });

  const allMaps = modes.flatMap((mode) => mode.maps);
  const advantages = allMaps
    .filter((map) => map.overviewEligible && typeof map.delta === 'number' && map.delta > 0)
    .sort((a, b) => b.delta - a.delta || b.combinedPlayed - a.combinedPlayed)
    .slice(0, 3);
  const threats = allMaps
    .filter((map) => map.overviewEligible && typeof map.delta === 'number' && map.delta < 0)
    .sort((a, b) => a.delta - b.delta || b.combinedPlayed - a.combinedPlayed)
    .slice(0, 3);

  const opponentRecord = calculateRecord(theirRows);
  const opponentModesByStrength = modes
    .filter((mode) => mode.opponent.played > 0)
    .sort((a, b) => strengthScore(b.opponent) - strengthScore(a.opponent) || b.opponent.played - a.opponent.played);
  const opponentMaps = allMaps.filter((map) => map.opponent.played > 0);
  const opponentStrongestMaps = [...opponentMaps]
    .sort((a, b) => b.opponent.strengthScore - a.opponent.strengthScore || b.opponent.played - a.opponent.played)
    .slice(0, 5);
  const opponentWeakestMaps = [...opponentMaps]
    .filter((map) => map.opponent.played >= 3)
    .sort((a, b) => a.opponent.winRate - b.opponent.winRate || b.opponent.played - a.opponent.played)
    .slice(0, 5);
  const opponentMostPlayedMaps = [...opponentMaps]
    .sort((a, b) => b.opponent.played - a.opponent.played || b.opponent.winRate - a.opponent.winRate)
    .slice(0, 5);
  const opponentLimitedMaps = [...opponentMaps]
    .filter((map) => map.opponent.played <= 2)
    .sort((a, b) => a.opponent.played - b.opponent.played || a.map.localeCompare(b.map))
    .slice(0, 5);

  return {
    datasetFilter,
    modes,
    advantages,
    threats,
    opponent: {
      overall: opponentRecord,
      recent: calculateRecentForm(theirRows, 10),
      strongestMode: opponentModesByStrength[0] || null,
      weakestMode: [...opponentModesByStrength]
        .filter((mode) => mode.opponent.played >= 3)
        .sort((a, b) => a.opponent.winRate - b.opponent.winRate || b.opponent.played - a.opponent.played)[0] || null,
      modes,
      strongestMaps: opponentStrongestMaps,
      weakestMaps: opponentWeakestMaps,
      mostPlayedMaps: opponentMostPlayedMaps,
      limitedDataMaps: opponentLimitedMaps,
      insights: deriveOpponentInsights({
        strongestMode: opponentModesByStrength[0] || null,
        strongestMaps: opponentStrongestMaps,
        weakestMaps: opponentWeakestMaps,
        mostPlayedMaps: opponentMostPlayedMaps,
      }),
    },
    sourceCounts: {
      ourRows: ourRows.length,
      opponentRows: theirRows.length,
    },
  };
};

export const deriveOpponentInsights = ({
  strongestMode,
  strongestMaps = [],
  weakestMaps = [],
  mostPlayedMaps = [],
} = {}) => {
  const insights = [];

  const mostPlayed = mostPlayedMaps[0];
  if (mostPlayed) {
    insights.push(`${mostPlayed.map} appears most frequently in recorded ${mostPlayed.modeLabel} results with ${mostPlayed.opponent.played} appearances.`);
  }

  const strongest = strongestMaps[0];
  if (strongest) {
    insights.push(`${strongest.map} is one of the strongest recorded ${strongest.modeLabel} maps at ${formatRecord(strongest.opponent)} and ${formatPercent(strongest.opponent.winRate)} WR.`);
  }

  const weakest = weakestMaps[0];
  if (weakest) {
    insights.push(`${weakest.map} is one of the lower-performing recorded ${weakest.modeLabel} maps at ${formatRecord(weakest.opponent)} and ${formatPercent(weakest.opponent.winRate)} WR.`);
  }

  if (strongestMode) {
    insights.push(`${strongestMode.label} is the strongest recorded mode by confidence-adjusted win rate.`);
  }

  return insights.slice(0, 4);
};
